import { format } from 'date-fns'
import { useCallback } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'

import { getOverlandVisits } from '@/api/services/overland'
import type { OverlandVisit } from '@/api/types/overland'
import { HistoryCollapsible } from '@/components/shared/history-collapsible'
import { Badge } from '@/components/ui/badge'
import { formatSecondsJa } from '@/lib/time-format'

type Props = {
  placeId: number
  currentVisitId?: number
  onClose: () => void
}

/** 到着〜出発の滞在秒数を返す（到着日なしなら null） */
const calcDurationSec = (v: OverlandVisit): number | null => {
  if (!v.arrival_date) return null
  const arrivalTime = new Date(v.arrival_date).getTime()
  const departureTime = v.departure_date ? new Date(v.departure_date).getTime() : Date.now()
  const sec = Math.floor((departureTime - arrivalTime) / 1000)
  return sec >= 60 ? sec : null
}

/** 訪問履歴のCollapsibleリスト */
export const VisitHistoryCollapsible = ({ placeId, currentVisitId, onClose }: Props) => {
  const [, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()

  const handleVisitClick = useCallback(
    (v: OverlandVisit) => {
      if (currentVisitId !== undefined && v.id === currentVisitId) return
      if (!v.arrival_date) return
      const dateStr = format(new Date(v.arrival_date), 'yyyy-MM-dd')
      onClose()
      if (!location.pathname.startsWith('/activity')) {
        navigate(`/activity?date=${dateStr}`)
        return
      }
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev)
        newParams.set('date', dateStr)
        return newParams
      })
    },
    [currentVisitId, onClose, setSearchParams, navigate, location.pathname],
  )

  const fetchItems = useCallback(() => getOverlandVisits(undefined, 500, placeId), [placeId])

  const handleError = useCallback(() => {
    toast.error('訪問履歴の取得に失敗しました')
  }, [])

  return (
    <HistoryCollapsible<OverlandVisit>
      label="訪問日一覧を表示"
      emptyMessage="訪問履歴がありません"
      fetchItems={fetchItems}
      getDateKey={(v) => (v.arrival_date ? format(new Date(v.arrival_date), 'yyyy-MM-dd') : 'unknown')}
      formatDateHeader={(dateKey) => (dateKey !== 'unknown' ? format(new Date(dateKey), 'yyyy/M/d (E)') : '不明')}
      getItemKey={(v) => v.id}
      renderItem={(v) => {
        const isCurrent = currentVisitId !== undefined && v.id === currentVisitId
        return (
          <button
            type="button"
            onClick={() => handleVisitClick(v)}
            disabled={isCurrent}
            className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-md w-full text-left transition-colors ${isCurrent ? 'bg-primary/10 font-medium' : 'bg-muted/30 hover:bg-muted/60 cursor-pointer'}`}
          >
            <span className="text-xs">
              {v.arrival_date ? format(new Date(v.arrival_date), 'HH:mm') : ''}
              {' → '}
              {v.departure_date ? format(new Date(v.departure_date), 'HH:mm') : '滞在中'}
              {(() => {
                const sec = calcDurationSec(v)
                if (!sec) return null
                return <span className="text-[11px] text-muted-foreground ml-1">({formatSecondsJa(sec)})</span>
              })()}
            </span>
            {isCurrent && (
              <Badge variant="outline" className="ml-auto text-[10px] h-4 px-1">
                現在
              </Badge>
            )}
          </button>
        )
      }}
      onError={handleError}
    />
  )
}
